import {
  CloseOutlined,
  EditTwoTone,
  InfoCircleOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { Button, Checkbox, Col, Divider, Form, Input, InputNumber, Row, Select, Space, Tooltip } from "antd";
import PracticesService, {
  ClientReviewerUserOptionsType,
  ClientReviewerUserOptionsTypeList,
  DefaultMissCallAgent,
  DefaultMissCallAgentTypeList,
  IPracticeAppFeature,
  IPracticeAppSubFeature,
  IPracticeParams,
  PracticeUpdateTypes,
} from "../../Services/PracticesService";
import { Utils } from "../../Utilities/Utils";
import React, { useEffect, useState } from "react";
import "./Practices.css";
import { adminContext } from "../../Common/PageRoute";
import { useUser } from "../../Context/UserContext";

export enum InvoiceDuePeriodType {
  Days = 0,
  Weeks = 1,
  Months = 2,
  Years = 3,
}

export const InvoiceDuePeriodTypeOptions = [
  { label: "Day(s)", value: InvoiceDuePeriodType.Days },
  { label: "Week(s)", value: InvoiceDuePeriodType.Weeks },
  { label: "Month(s)", value: InvoiceDuePeriodType.Months },
  { label: "Year(s)", value: InvoiceDuePeriodType.Years },
];

export enum SecureLinkExpiryType {
  Unknown = 0,
  SevenDaysExpiry = 1,
  FourteenDaysExpiry = 2,
  TwentyOneDaysExpiry = 3,
}

export const SecureLinkExpiryTypeOptions = [
  { label: "None", value: SecureLinkExpiryType.Unknown },
  { label: "7 days", value: SecureLinkExpiryType.SevenDaysExpiry },
  { label: "14 days", value: SecureLinkExpiryType.FourteenDaysExpiry },
  { label: "21 days", value: SecureLinkExpiryType.TwentyOneDaysExpiry },
];

export enum LOERenewalYearRange {
  Custom = 0,
  Never = 1,
  OneYear = 2,
  TwoYears = 3,
  ThreeYears = 4,
  FourYears = 5,
  FiveYears = 6,
}

export const LOERenewalYearOptions = [
  { label: "Custom", value: LOERenewalYearRange.Custom },
  { label: "Never", value: LOERenewalYearRange.Never },
  { label: "1 year", value: LOERenewalYearRange.OneYear },
  { label: "2 years", value: LOERenewalYearRange.TwoYears },
  { label: "3 years", value: LOERenewalYearRange.ThreeYears },
  { label: "4 years", value: LOERenewalYearRange.FourYears },
  { label: "5 years", value: LOERenewalYearRange.FiveYears },
];

const Features = ({
  isEdit,
  setIsEdit,
  setLoading,
  practiceId,
}: {
  isEdit: { type: PracticeUpdateTypes };
  setIsEdit: React.Dispatch<
    React.SetStateAction<{ type: PracticeUpdateTypes }>
  >;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  practiceId: number;
}) => {
  const [form] = Form.useForm();
  const { openNotification } = React.useContext(adminContext);
  const { isSubAdmin } = useUser();
  const isViewOnly = isSubAdmin();
  const secureLinkEnabled = Form.useWatch(
    ["appSubFeature", "secureLink"],
    form,
  );
  const [initialValues, setInitialValues] = useState<IPracticeAppSubFeature>();

  React.useEffect(() => {
    if (secureLinkEnabled) return;

    const current = form.getFieldValue("appSubFeature") || {};
    if (
      current.secureLinkExpiry &&
      current.secureLinkExpiry !== SecureLinkExpiryType.Unknown
    ) {
      form.setFieldsValue({
        appSubFeature: {
          ...current,
          secureLinkExpiry: SecureLinkExpiryType.Unknown,
        },
      });
    }
  }, [secureLinkEnabled, form]);

  const getPracticeFeatures = async () => {
    try {
      setLoading(true);
      const res = await PracticesService.getPracticeFeatureDetails(practiceId);
      form.setFieldsValue(res.result);
      setInitialValues(res.result);
    } catch (error) {
      console.error("Error fetching practice features:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getPracticeFeatures();
  }, []);

  const saveFeatureInformation = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      let request: Promise<any> | null = null;

      if (isEdit.type === PracticeUpdateTypes.features) {
        const params: IPracticeAppFeature = values.appFeature;
        request = PracticesService.updatePracticeFeatures(practiceId, params);
      }

      if (isEdit.type === PracticeUpdateTypes.praticeProperites) {
        const unitsPerHour = values.mtsDailyUnitCapacity?.unitsPerHour;
        if (unitsPerHour === "" || unitsPerHour === 0 || unitsPerHour > 60) {
          openNotification(
            "error",
            "Units per hour must be between 1 and 60 before saving.",
          );
          return;
        }

        const params: {
          params: IPracticeParams;
          appSubFeature: IPracticeAppSubFeature;
          CreditNoteReviewAmount: number;
          DueDateConfig: {
            number: number;
            period: number;
          };
          ClientReviewerPracticeProfile: {
            userOption: ClientReviewerUserOptionsType;
          };
          LOERenewalYear: number;
          clientLogoDimensions: {
            width: number;
            height: number;
          };
          mtsDailyUnitCapacity:{
            unitsPerHour: number;
          };
          assignMissedCallUserOption: DefaultMissCallAgent;
        } = {
          params: values.params,
          appSubFeature: values.appSubFeature,
          CreditNoteReviewAmount: values.creditNoteReviewAmount,
          DueDateConfig: values.dueDateConfig,
          LOERenewalYear: values.loeRenewalYear,
          ClientReviewerPracticeProfile: values.clientReviewerPracticeProfile,
          clientLogoDimensions: {
            width: values.clientLogoDimensions?.width || 0,
            height: values.clientLogoDimensions?.height || 0,
          },
          mtsDailyUnitCapacity: {
            unitsPerHour,
          },
          assignMissedCallUserOption: values.assignMissedCallUserOption,
        };
        request = PracticesService.updatePracticeProperties(practiceId, params);
      }

      if (!request) return;

      const res = await request;

      if (res?.result) {
        openNotification("success", "Information updated successfully");
        setIsEdit({ type: PracticeUpdateTypes.unknown });
        setInitialValues(values);
        getPracticeFeatures();
      } else {
        openNotification(
          "error",
          res?.message || "Error Practice Feature information",
        );
      }
    } catch {
      openNotification("error", "Error updating information");
    } finally {
      setLoading(false);
    }
  };
  const handleCancel = () => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
    setIsEdit({ type: PracticeUpdateTypes.unknown });
  };
  return (
    <Form form={form} disabled={isViewOnly} requiredMark={false} colon={false}>
      {/* Features */}
      <Col className="card border pb-3 border-rounded-1 bg-secondary ">
        <Row
          className="border-bottom px-2 py-1 font-bold"
          justify="space-between"
          align="middle"
        >
          <Col>Features</Col>
          {!isViewOnly && (
            <Col className="d-flex align-items-center">
              {/* <Checkbox
              className="me-2 mt-2"
              disabled={isEdit.type !== PracticeUpdateTypes.features}
              checked={ALL_FEATURE_KEYS.every((key) =>
                form.getFieldValue(["appFeature", key]),
              )}
              indeterminate={
                ALL_FEATURE_KEYS.some((key) =>
                  form.getFieldValue(["appFeature", key]),
                ) &&
                !ALL_FEATURE_KEYS.every((key) =>
                  form.getFieldValue(["appFeature", key]),
                )
              }
              onChange={(e) => {
                const checked = e.target.checked;
                const updated: Partial<IPracticeAppFeature> = {};

                ALL_FEATURE_KEYS.forEach((key) => {
                  updated[key] = checked;
                });

                form.setFieldsValue({
                  appFeature: {
                    ...form.getFieldValue("appFeature"),
                    ...updated,
                  },
                });
              }}
            >
              Select All
            </Checkbox> */}

              <Button
                icon={<EditTwoTone />}
                type="text"
                style={{ border: "none" }}
                onClick={() =>
                  setIsEdit({ type: PracticeUpdateTypes.features })
                }
              />
            </Col>
          )}
        </Row>
        <Row className="px-3 pt-3">
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appFeature", "calls"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.features ? false : true
                }
                className="line-height-28"
              >
                Calls
              </Checkbox>
            </Form.Item>
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appFeature", "leadSources"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.features ? false : true
                }
                className="line-height-28"
              >
                Lead sources
              </Checkbox>
            </Form.Item>
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appFeature", "linkedinLeads"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.features ? false : true
                }
                className="line-height-28"
              >
                LinkedIn leads
              </Checkbox>
            </Form.Item>
          </Col>

          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appFeature", "webForms"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.features ? false : true
                }
                className="line-height-28"
              >
                Web forms
              </Checkbox>
            </Form.Item>
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appFeature", "dayEnd"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.features ? false : true
                }
                className="line-height-28"
              >
                Days end
              </Checkbox>
            </Form.Item>
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appFeature", "accounts"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.features ? false : true
                }
                className="line-height-28"
              >
                Accounts
              </Checkbox>
            </Form.Item>
          </Col>

          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appFeature", "corporationTax"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.features ? false : true
                }
                className="line-height-28"
              >
                Corporation tax
              </Checkbox>
            </Form.Item>
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appFeature", "aml"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.features ? false : true
                }
                className="line-height-28"
              >
                AML
              </Checkbox>
            </Form.Item>
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appFeature", "chats"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.features ? false : true
                }
                className="line-height-28"
              >
                Chats
              </Checkbox>
            </Form.Item>
          </Col>

          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appFeature", "teams"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.features ? false : true
                }
                className="line-height-28"
              >
                Teams
              </Checkbox>
            </Form.Item>
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appFeature", "bankStatement"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.features ? false : true
                }
                className="line-height-28"
              >
                Bank statements
              </Checkbox>
            </Form.Item>
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appFeature", "payroll"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.features ? false : true
                }
                className="line-height-28"
              >
                Payroll
              </Checkbox>
            </Form.Item>
          </Col>

          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appFeature", "clientDataRequest"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.features ? false : true
                }
                className="line-height-28"
              >
                Client data request
              </Checkbox>
            </Form.Item>
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appFeature", "bookkeeping"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.features ? false : true
                }
                className="line-height-28"
              >
                Bookkeeping
              </Checkbox>
            </Form.Item>
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appFeature", "timeSheet"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.features ? false : true
                }
                className="line-height-28"
              >
                TimeSheet
              </Checkbox>
            </Form.Item>
          </Col>

          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appFeature", "careOf"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.features ? false : true
                }
                className="line-height-28"
              >
                CareOf
              </Checkbox>
            </Form.Item>
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appFeature", "selfAssessment"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.features ? false : true
                }
                className="line-height-28"
              >
                Self assessement
              </Checkbox>
            </Form.Item>
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appFeature", "dms"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.features ? false : true
                }
                className="line-height-28"
              >
                DMS
              </Checkbox>
            </Form.Item>
          </Col>

          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appFeature", "audit"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.features ? false : true
                }
                className="line-height-28"
              >
                Audit
              </Checkbox>
            </Form.Item>
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appFeature", "companiesHouseSync"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.features ? false : true
                }
                className="line-height-28"
              >
                CompaniesHouse Sync
              </Checkbox>
            </Form.Item>
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appFeature", "other1"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.features ? false : true
                }
                className="line-height-28"
              >
                Other1
              </Checkbox>
            </Form.Item>
          </Col>

          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appFeature", "other2"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.features ? false : true
                }
                className="line-height-28"
              >
                Other2
              </Checkbox>
            </Form.Item>
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appFeature", "other3"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.features ? false : true
                }
                className="line-height-28"
              >
                Other3
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>
        {!isViewOnly && isEdit.type === PracticeUpdateTypes.features && (
          <Row className="px-3 pt-3">
            <Button type="primary" onClick={saveFeatureInformation}>
              <SaveOutlined /> Save
            </Button>
            <Button className="ms-2" onClick={handleCancel}>
              <CloseOutlined /> Cancel
            </Button>
          </Row>
        )}
      </Col>

      {/* Practice propertie */}
      <Col className="card border pb-3 mt-3 border-rounded-1 bg-secondary ">
        <Row
          className="border-bottom px-2 py-1 font-bold"
          justify="space-between"
          align="middle"
        >
          <Col> Practice properties</Col>
          {!isViewOnly && (
            <Col>
              <div className="d-flex align-items-center">
                {/* <Checkbox
                className="me-2 mt-2"
                disabled={isEdit.type !== PracticeUpdateTypes.praticeProperites}
                checked={isAllFeaturesSelected()}
                indeterminate={
                  isSomeFeaturesSelected() && !isAllFeaturesSelected()
                }
                onChange={(e) => onSelectAllFeaturesChange(e.target.checked)}
              >
                Select All
              </Checkbox> */}

                <Button
                  icon={<EditTwoTone />}
                  type="text"
                  style={{ border: "none" }}
                  onClick={() =>
                    setIsEdit({
                      type: PracticeUpdateTypes.praticeProperites,
                    })
                  }
                />
              </div>
            </Col>
          )}
        </Row>
        {/* Parameters */}
        <Col className="font-bold d-flex px-3 pt-1" span={24}>
          <Divider
            orientation="left"
            orientationMargin="0"
            style={{ margin: "0px 0px 0px 0px" }}
          >
            <p style={{ fontSize: "14px", fontWeight: 500 }}>Parameters</p>
          </Divider>
        </Col>

        <Row className="px-3">
          <Col lg={8} sm={12} xs={24}>
            <Form.Item
              name={["params", "pageTrackTimeout"]}
              layout="vertical"
              label={
                <span style={{ paddingTop: "8px" }}>
                  Page track timeout
                  <Tooltip
                    id="tooltip-ptt"
                    overlayClassName="white-tooltip"
                    title="The duration of user inactivity, measured in seconds, after which the user is marked as inactive and the timeout period begins."
                  >
                    <InfoCircleOutlined style={{ fontSize: "13px", marginLeft: "6px" }} />
                  </Tooltip>
                </span>
              }
              wrapperCol={{ xxl: 10, xl: 12, sm: 20, xs: 22 }}
            // className="custom-form-item"
            >
              <Input
                // className="line-height-28"
                placeholder="Enter value"
                disabled={
                  isEdit.type === PracticeUpdateTypes.praticeProperites
                    ? false
                    : true
                }
                onKeyPress={(e) => Utils.ValidateNumberOnly(e)}
              />
            </Form.Item>
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              name={"loeRenewalYear"}
              layout="vertical"
              label={
                <span style={{ paddingTop: "8px" }}>
                  LOE renewal date
                  <Tooltip
                    id="tooltip-ptt"
                    overlayClassName="white-tooltip"
                    title="Date on which the LOE expires."
                  >
                    <InfoCircleOutlined
                      style={{ fontSize: "13px", marginLeft: "6px" }}
                    />
                  </Tooltip>
                </span>
              }
              wrapperCol={{ xxl: 10, xl: 12, sm: 20, xs: 22 }}
              style={{ width: "100%" }}
            >
              <Select
                disabled={
                  isEdit.type === PracticeUpdateTypes.praticeProperites
                    ? false
                    : true
                }
                style={{
                  width: "100%",
                  fontWeight: "bolder",
                  // height: "28px",
                }}
                options={LOERenewalYearOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Billing and finance */}
        <Col className="font-bold d-flex px-3 pt-2" span={24}>
          <Divider
            orientation="left"
            orientationMargin="0"
            style={{ margin: "0px 0px 0px 0px" }}
          >
            <p style={{ fontSize: "14px", fontWeight: 500 }}>
              Billing and finance
            </p>
          </Divider>
        </Col>

        <Row className="px-3 ">
          <Col lg={8} sm={12} xs={24}>
            <Form.Item
              name={["params", "dueInvoicesTaskDays"]}
              // className="custom-form-item"
              layout="vertical"
              label={
                <span style={{ paddingTop: "8px" }}>
                  Create overdue invoice task
                  <Tooltip
                    id="tooltip-ptt"
                    overlayClassName="white-tooltip"
                    title="Number of days after which a task is created, once the invoice becomes due."
                  >
                    <InfoCircleOutlined style={{ fontSize: "13px", marginLeft: "6px" }} />
                  </Tooltip>
                </span>
              }
              wrapperCol={{ xxl: 10, xl: 12, sm: 20, xs: 22 }}
            >
              <Input
                // size="small"
                className="line-height-28"
                addonAfter="DAY(S)"
                style={{ width: "100%", height: "28px", }}
                placeholder="Enter value"
                disabled={
                  isEdit.type === PracticeUpdateTypes.praticeProperites
                    ? false
                    : true
                }
                onKeyPress={(e) => Utils.ValidateNumberOnly(e)}
              />
            </Form.Item>
          </Col>

          <Col lg={8} sm={12} xs={24}>
            <Form.Item
              name={["params", "taskDueDateInDays"]}
              // className="custom-form-item"
              layout="vertical"
              label={
                <span style={{ paddingTop: "8px" }}>
                  Invoice tasks due date
                  <Tooltip
                    id="tooltip-ptt"
                    overlayClassName="white-tooltip"
                    title="Due date for invoice tasks will be set to 'n' number of days after creation of task."
                  >
                    <InfoCircleOutlined
                      style={{ fontSize: "13px", marginLeft: "6px" }}
                    />
                  </Tooltip>
                </span>
              }
              wrapperCol={{ xxl: 10, xl: 12, sm: 20, xs: 22 }}
            >
              {/* {<span style={{ backgroundColor: "red" }}> */}
              <Input
                className="line-height-28"
                addonAfter="DAY(S)"
                style={{ width: "100%", height: "28px" }}
                placeholder="Enter value"
                disabled={
                  isEdit.type === PracticeUpdateTypes.praticeProperites
                    ? false
                    : true
                }
                onKeyPress={(e) => Utils.ValidateNumberOnly(e)}
              />
            </Form.Item>
          </Col>

          <Col lg={8} sm={12} xs={24}>
            <Form.Item
              name={["creditNoteReviewAmount"]}
              // className="custom-form-item"
              layout="vertical"
              label={
                <span style={{ paddingTop: "8px" }}>
                  <span>Credit note review amount</span>
                  <Tooltip
                    id="tooltip-ptt"
                    overlayClassName="white-tooltip"
                    title="The threshold beyond which a credit note needs to be routed through review mechanisms"
                  >
                    <InfoCircleOutlined
                      style={{ fontSize: "13px", marginLeft: "6px" }}
                    />
                  </Tooltip>
                </span>
              }
              wrapperCol={{ xxl: 10, xl: 12, sm: 20, xs: 22 }}
            >
              <Input
                className="line-height-28"
                addonBefore="£"
                style={{ width: "100%" }}
                placeholder="Enter value"
                disabled={
                  isEdit.type === PracticeUpdateTypes.praticeProperites
                    ? false
                    : true
                }
                onKeyPress={(e) => Utils.ValidateNumberOnly(e)}
              />
            </Form.Item>
          </Col>

          <Col lg={8} sm={12} xs={24}>
            <Form.Item
              className="custom-form-item"
              label={
                <span style={{ paddingTop: "8px" }}>
                  <span>Invoice due date</span>
                  <Tooltip
                    id="tooltip-ptt"
                    overlayClassName="white-tooltip"
                    title="Date on which the invoice becomes due."
                  >
                    <InfoCircleOutlined
                      style={{ fontSize: "13px", marginLeft: "6px" }}
                    />
                  </Tooltip>
                </span>
              }
              labelCol={{ span: 24 }}
              wrapperCol={{ xxl: 10, xl: 12, sm: 20, xs: 22 }}
            >
              <Space.Compact style={{ width: "100%" }}>
                <Form.Item
                  name={["dueDateConfig", "number"]}
                  layout="vertical"
                  className="custom-form-item"
                >
                  <Input
                    // className="line-height-28"
                    style={{ width: "100%" }}
                    placeholder="Enter value"
                    disabled={
                      isEdit.type === PracticeUpdateTypes.praticeProperites
                        ? false
                        : true
                    }
                    onKeyPress={(e) => Utils.ValidateNumberOnly(e)}
                  />
                </Form.Item>

                <Form.Item
                  name={["dueDateConfig", "period"]}
                  // layout="vertical"
                  style={{ width: "100%" }}
                  className="custom-form-item"
                  // labelCol={{ span: 24 }}
                  // wrapperCol={{ span: 24 }}
                >
                  <Select
                    disabled={
                      isEdit.type === PracticeUpdateTypes.praticeProperites
                        ? false
                        : true
                    }
                    style={{
                      width: "100%",
                      fontWeight: "bolder",
                      // height: "28px",
                    }}
                    options={InvoiceDuePeriodTypeOptions}
                  />
                </Form.Item>
              </Space.Compact>
            </Form.Item>
          </Col>
        </Row>

        <Row className="px-3 pt-1 ">
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Without discount"
              title="Disable discount function based on practice requirements."
              name={["appSubFeature", "withoutDiscount"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Receipt attachments"
              title="File attachment for recording manual receipt of invoices would be made mandatory."
              name={["appSubFeature", "receiptAttachments"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Bank details"
              title="To show bank details in invoice."
              name={["appSubFeature", "bankDetails"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Collection method"
              title="Collection method in invoice."
              name={["appSubFeature", "collectionMethod"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Charge date"
              title="Gather customer invoices on a designated date, adding an extra field to each client's profile, and transmit this information via API to GoCardless."
              name={["appSubFeature", "chargeDay"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Block invoice editing"
              title="This feature helps to block editing of invoices once the invoice date is passed."
              name={["appSubFeature", "blockInvoiceEdit"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Client reference in invoice view"
              title="This feature helps to show client reference in invoice pdf."
              name={["appSubFeature", "clientReferenceInInvoice"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Cardnet"
              title="Cardnet is a payment gateway that enables businesses to process online credit and debit card transactions securely."
              name={["appSubFeature", "cardNet"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Credit note review"
              title="This feature enables a review request to be sent, when the amount of credit note exceeds the threshold provided in the credit note review amount."
              name={["appSubFeature", "creditNoteReviewRequest"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Review and process auto refund"
              title="This feature helps to cater situations where there is separation of duties for credit note creation/approval(in case of review mechanisms) and processing of funds."
              name={["appSubFeature", "reviewAutoRefund"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Notify client on subscription update"
              title="This feature enables the option to send email notifications to clients when their subscription details are updated."
              name={["appSubFeature", "notifyClientOnSubscriptionUpdate"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Record transfer"
              title={`Enable this option to allow recording of bank transfers on the client's payment side. Once enabled, the "Record transfer" button will appear during payments.`}
              name={["appSubFeature", "bankTransfer"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Attachment for record transfer"
              title="This feature makes attachment upload compulsory during bank transfer recording."
              name={["appSubFeature", "recordTransferAttachment"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
        </Row>
        {/*  Manual timesheets */}
        {form.getFieldValue(["appFeature", "timeSheet"]) && (
          <>
            <Col className="font-bold d-flex px-3 pt-1" span={24}>
              <Divider
                orientation="left"
                orientationMargin="0"
                style={{ margin: "0px 0px 0px 0px" }}
              >
                <p style={{ fontSize: "14px", fontWeight: 500 }}>
                  Manual timesheets
                </p>
              </Divider>
            </Col>

            <Row className="px-3 pt-1 ">
              <Col lg={8} sm={12} xs={24}>
                <div className="d-flex">
                  <Form.Item
                    className="custom-form-item"
                    name={["appSubFeature", "manualTimesheet"]}
                    valuePropName="checked"
                  >
                    <Checkbox
                      disabled={
                        isEdit.type === PracticeUpdateTypes.praticeProperites
                          ? false
                          : true
                      }
                      // onChange={(e) => setIsManualTimesheet(e.target.checked)}
                      className="line-height-28"
                    >
                      Manual timesheet
                    </Checkbox>
                  </Form.Item>
                  <Tooltip
                    id="tooltip-ptt"
                    overlayClassName="white-tooltip"
                    title={
                      "Automatically create badges for number of hours worked to help process billings for any given client."
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <InfoCircleOutlined
                      style={{
                        fontSize: "13px",
                        marginTop: "-4px",
                      }}
                    />
                  </Tooltip>
                </div>
              </Col>
              {/* {isManualTimesheet && } */}
              <Form.Item
                noStyle
                shouldUpdate={(preValues, currValues) =>
                  preValues?.appSubFeature?.manualTimesheet !==
                  currValues?.appSubFeature?.manualTimesheet
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue(["appSubFeature", "manualTimesheet"]) ? (
                    <Col lg={8} sm={12} xs={12}>
                      <Form.Item
                        style={{ marginBottom: "14px" }}
                        name={["mtsDailyUnitCapacity", "unitsPerHour"]}
                        // layout="vertical"
                        label={
                          <span>
                            Units per hour
                            <Tooltip
                              id="tooltip-ptt"
                              overlayClassName="white-tooltip"
                              title="Please ensure the WIP is processed for all open timesheets and evaluate the risk attached on all the manual timesheet reports before changing the units per hour"
                            >
                              <InfoCircleOutlined
                                style={{ fontSize: "13px", marginLeft: "6px" }}
                              />
                            </Tooltip>
                          </span>
                        }
                        wrapperCol={{ xxl: 8, xl: 12, sm: 12 }}
                        rules={[
                          {
                            required: true,
                            message: "Please enter units per hour",
                          },
                          {
                            validator: (_, value) => {
                              if (
                                value === undefined ||
                                value === null ||
                                value === ""
                              ) {
                                return Promise.resolve();
                              }

                              const numberValue = Number(value);

                              if (numberValue > 0 && numberValue <= 60) {
                                return Promise.resolve();
                              }

                              return Promise.reject(
                                new Error("Enter between 1–60"),
                              );
                            },
                          },
                        ]}
                      >
                        <Input
                          placeholder="Enter value"
                          addonAfter="Unit(s)"
                          style={{ width: "100%" }}
                          //  ||                      isEdit.type === PracticeUpdateTypes.praticeProperites
                          disabled={
                            isEdit.type ===
                            PracticeUpdateTypes.praticeProperites
                              ? false
                              : true
                          }
                          onKeyPress={(e) => Utils.ValidateNumberOnly(e)}
                        />
                      </Form.Item>
                    </Col>
                  ) : (
                    <Col lg={8} sm={12} xs={12}>
                      <Form.Item
                        style={{ marginBottom: "14px" }}
                        name={["mtsDailyUnitCapacity", "unitsPerHour"]}
                        label={
                          <span>
                            Units per hour
                            <Tooltip
                              id="tooltip-ptt"
                              overlayClassName="white-tooltip"
                              title="Please ensure the WIP is processed for all open timesheets and evaluate the risk attached on all the manual timesheet reports before changing the units per hour"
                            >
                              <InfoCircleOutlined
                                style={{ fontSize: "13px", marginLeft: "6px" }}
                              />
                            </Tooltip>
                          </span>
                        }
                        wrapperCol={{ xxl: 8, xl: 12, sm: 12 }}
                      >
                        <Input
                          placeholder="Enter value"
                          addonAfter="Unit(s)"
                          style={{ width: "100%" }}
                          disabled={true}
                        />
                      </Form.Item>
                    </Col>
                  )
                }
              </Form.Item>

              <Col lg={8} xs={0}></Col>

              <Col lg={8} sm={12} xs={24}>
                <div className="d-flex">
                  <Form.Item
                    className="custom-form-item"
                    name={[
                      "appSubFeature",
                      "mtsAutomationSettings",
                      "automate",
                    ]}
                    valuePropName="checked"
                  >
                    <Checkbox
                      disabled={
                        isEdit.type === PracticeUpdateTypes.praticeProperites
                          ? false
                          : true
                      }
                      className="line-height-28"
                    >
                      Automate WIP
                    </Checkbox>
                  </Form.Item>
                  <Tooltip
                    id="tooltip-ptt"
                    overlayClassName="white-tooltip"
                    title="Automatically create badges for number of hours worked to help process billings for any given client."
                  >
                    <InfoCircleOutlined
                      style={{ fontSize: "13px", marginTop: "-4px" }}
                    />
                  </Tooltip>
                </div>
              </Col>

              <Form.Item
                noStyle
                shouldUpdate={(preValues, currValues) =>
                  preValues?.appSubFeature?.mtsAutomationSettings?.automate !==
                  currValues?.appSubFeature?.mtsAutomationSettings?.automate
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue([
                    "appSubFeature",
                    "mtsAutomationSettings",
                    "automate",
                  ]) && (
                    <Col lg={8} sm={12} xs={24}>
                      <div className="d-flex">
                        <Form.Item
                          colon={false}
                          // style={{ marginTop: -5 }}
                          // wrapperCol={{ span: 5 }}
                          className="custom-form-item"
                          label={
                            <span>
                              Create WIPs after (days)
                              <Tooltip
                                id="tooltip-ptt"
                                overlayClassName="white-tooltip"
                                title="Automatic WIP badge is created after selected number of days to allocate against the invoices to be issued."
                              >
                                <InfoCircleOutlined
                                  style={{
                                    fontSize: "13px",
                                    marginLeft: "6px",
                                  }}
                                />
                              </Tooltip>
                            </span>
                          }
                          name={["appSubFeature", "", "days"]}
                          rules={[
                            {
                              validator: (_, value) => {
                                if (
                                  value === undefined ||
                                  value === null ||
                                  value === ""
                                ) {
                                  return Promise.resolve();
                                }
                                if (Number(value) <= 0) {
                                  return Promise.reject(
                                    new Error("Days must be greater than 0"),
                                  );
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <Input
                            // className="line-height-28"
                            placeholder="Days"
                            // style={{ marginTop: 2 }}
                            style={{
                              width: "105px",
                              // marginTop: 2,
                            }}
                            onKeyPress={(e) => Utils.ValidateNumberOnly(e)}
                            disabled={
                              isEdit.type ===
                              PracticeUpdateTypes.praticeProperites
                                ? false
                                : true
                            }
                          />
                        </Form.Item>
                      </div>
                    </Col>
                  )
                }
              </Form.Item>
            </Row>
          </>
        )}

        {/* Additional features */}
        <Col className="font-bold d-flex px-3 pt-1" span={24}>
          <Divider
            orientation="left"
            orientationMargin="0"
            style={{ margin: "0px 0px 0px 0px" }}
          >
            <p style={{ fontSize: "14px", fontWeight: 500 }}>
              {" "}
              Additional features
            </p>
          </Divider>
        </Col>

        <Row className="px-3 pt-1 align-items-center">
          {/* <Col span={2} className="d-flex justify-content-end">
            <Checkbox
              disabled={isEdit.type !== PracticeUpdateTypes.praticeProperites}
              checked={additionalFeatureKeys.every((key) =>
                form.getFieldValue(["appSubFeature", key]),
              )}
              indeterminate={
                additionalFeatureKeys.some((key) =>
                  form.getFieldValue(["appSubFeature", key]),
                ) &&
                !additionalFeatureKeys.every((key) =>
                  form.getFieldValue(["appSubFeature", key]),
                )
              }
              onChange={(e) => {
                const checked = e.target.checked;
                const updated: any = {};
                additionalFeatureKeys.forEach((key) => {
                  updated[key] = checked;
                });
                form.setFieldsValue({
                  appSubFeature: {
                    ...form.getFieldValue("appSubFeature"),
                    ...updated,
                  },
                });
              }}
            >
              Select All
            </Checkbox>
          </Col> */}
        </Row>

        <Row className="px-3 pt-1">
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Digital signature without AML"
              title="Option to create e-signatures without processing  the AML."
              name={["appSubFeature", "digitalSignatureWithoutAML"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Website queries with OTP"
              title="Request OTP to validate phone number of visitors raising website queries."
              name={["appSubFeature", "websiteQueriesWithOTP"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Progressive leads"
              title="Track and update lead status through the creation of progressive steps to effectively manage leads."
              name={["appSubFeature", "progressiveLeads"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>

          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Action tab"
              title="Option to create a quick shortcut board for actions like confirmation statement filing, review requests, etc."
              name={["appSubFeature", "actionTab"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Schedule emails"
              title="Option to enable schedule email functionality."
              name={["appSubFeature", "scheduleEmail"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Resource section (Reports)"
              title="Enable the resource section within the practice."
              name={["appSubFeature", "resourceSection"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>

          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Client automation"
              title="Option to collect onboarding and service related details automatically once the payment is made."
              name={["appSubFeature", "clientAutomation"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Proof of earning (FPS)"
              title="File attachment for proof of earning on FPS submissions would be made mandatory."
              name={["appSubFeature", "proofOfEarningFPS"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Skip team update"
              title="Skip team update in client profile."
              name={["appSubFeature", "skipTeamUpdate"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>

          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item className="custom-form-item" noStyle>
              <div className="d-flex" style={{ alignItems: "center" }}>
                <Form.Item
                  className="custom-form-item"
                  name={["appSubFeature", "secureLink"]}
                  valuePropName="checked"
                >
                  <Checkbox
                    disabled={
                      isEdit.type === PracticeUpdateTypes.praticeProperites
                        ? false
                        : true
                    }
                    className="line-height-28"
                  >
                    Send attachments via secure link
                  </Checkbox>
                </Form.Item>

                <Form.Item name={["params", "secureLinkExpiry"]} noStyle>
                  <Select
                    disabled={
                      isEdit.type !== PracticeUpdateTypes.praticeProperites ||
                      !secureLinkEnabled
                    }
                    style={{
                      width: 80,
                      fontWeight: "bolder",
                      height: "28px",
                    }}
                    options={SecureLinkExpiryTypeOptions}
                  />
                </Form.Item>

                <Tooltip
                  id="tooltip-ptt"
                  overlayClassName="white-tooltip"
                  title="It will require OTP to access all the attachments except client invoices."
                >
                  <InfoCircleOutlined
                    style={{ fontSize: "13px", marginLeft: 8 }}
                  />
                </Tooltip>
              </div>
            </Form.Item>
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="AML for all officers"
              title="Generates AML report based on all officer count (including secretary).."
              name={["appSubFeature", "amLforAllOfficers"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Task deadline by team leader"
              title="Task deadline can only be updated by admin or team lead user."
              name={["appSubFeature", "allowTLandAdminToUpdateTaskDeadline"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>

          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Directors accounts"
              title="Director accounts creation on retrieving director."
              name={["appSubFeature", "directorAccountWhileRetrieve"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Review notifications to teamleaders"
              title="Review process notification for the teamleaders."
              name={["appSubFeature", "reviewNotificationToTeamleader"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Do not change task assignee when account manager changes"
              title="Prevent task assignee changes when team membership changes."
              name={["appSubFeature", "noChangeInTaskAssigneeWithTeamChange"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>

          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="LOE financial automation"
              title="If client automation subfeature is on, option to collect financial details automatically once the LOE is accepted."
              name={["appSubFeature", "loeFinancialAutomation"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Clients joiners and leavers"
              title="Verifying which clients have recently joined (joiners) or exited (leavers) to maintain an up-to-date record of client statuses."
              name={["appSubFeature", "clientsJoinersAndLeavers"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Masking"
              title="This feature helps to mask phone number and email address."
              name={["appSubFeature", "masking"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>

          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Add customer"
              title="This feature allows addition of customers directly, eliminating the need to go though lead process for specific customers."
              name={["appSubFeature", "addCustomer"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Preparation side validation"
              title="This feature allows the user to validate the APCT report on the preparer’s side."
              name={["appSubFeature", "preparationSideValidation"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Teams sales comparison"
              title="This feature helps to compare current and previous year sales for each team."
              name={["appSubFeature", "teamSalesComparison"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>

          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Working files (optional)"
              title="Once enabled, working files do not need to be attached to proceed with review requests."
              name={["appSubFeature", "reviewRequestAttachment"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Team group"
              title="Enable team group tab to organise teams and filter reports."
              name={["appSubFeature", "teamGroup"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Repeat last task structure"
              title="Automatically applies the parent steps and child tasks, along with their user assignments, from the most recently created task of the same type for the same client."
              name={["appSubFeature", "childTask"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>

          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Client inactive"
              title="This feature restricts marking clients as 'inactive', when invoices and tasks are due for action."
              name={["appSubFeature", "clientInactive"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Proposals"
              title="This feature helps to add and customize proposals."
              name={["appSubFeature", "proposalAndQuotes"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Show header only on the first page (LOE)"
              title="For LOE templates, the header will appear only on the first page and will not repeat on subsequent pages."
              name={["appSubFeature", "showHeaderOnlyOnFirstPage"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>

          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Single Accounts & Corporation Folder"
              title="Use one combined folder for Accounts and Corporation Tax Return instead of creating separate folders for each."
              name={["appSubFeature", "mergedAPCTDMS"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Task dates on weekends"
              title="Allows task dates to be set on weekends without validation errors."
              name={["appSubFeature", "taskDatesOnWeekends"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
        </Row>

        <Row className="px-3">
          <Col lg={8} sm={12} xs={24}>
            <Form.Item
              name={["clientReviewerPracticeProfile", "userOption"]}
              layout="vertical"
              label={
                <span style={{ paddingTop: "8px" }}>
                  Client Review
                  <Tooltip
                    id="tooltip-ptt"
                    overlayClassName="white-tooltip"
                    title="Default user to be credited for client reviews."
                  >
                    <InfoCircleOutlined
                      style={{ fontSize: "13px", marginLeft: "6px" }}
                    />
                  </Tooltip>
                </span>
              }
              wrapperCol={{ xxl: 10, xl: 12, sm: 20, xs: 22 }}
              // className="custom-form-item"
            >
              <Select
                style={{
                  width: "100%",
                  fontWeight: "bold",
                  // height: "28px",
                }}
                disabled={
                  isEdit.type === PracticeUpdateTypes.praticeProperites
                    ? false
                    : true
                }
                options={ClientReviewerUserOptionsTypeList}
              />
            </Form.Item>
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              name={"assignMissedCallUserOption"}
              layout="vertical"
              label={
                <span style={{ paddingTop: "8px" }}>
                  Missed call assignee
                  <Tooltip
                    id="tooltip-ptt"
                    overlayClassName="white-tooltip"
                    title="This feature assigns missed calls according to the selected criteria."
                  >
                    <InfoCircleOutlined
                      style={{ fontSize: "13px", marginLeft: "6px" }}
                    />
                  </Tooltip>
                </span>
              }
              wrapperCol={{ xxl: 10, xl: 12, sm: 20, xs: 22 }}
              style={{ width: "100%" }}
            >
              <Select
                disabled={
                  isEdit.type === PracticeUpdateTypes.praticeProperites
                    ? false
                    : true
                }
                style={{
                  width: "100%",
                  fontWeight: "bolder",
                  // height: "28px",
                }}
                options={DefaultMissCallAgentTypeList}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* AI Features */}
        <Col className="font-bold d-flex px-3 pt-1" span={24}>
          <Divider
            orientation="left"
            orientationMargin="0"
            style={{ margin: "0px 0px 0px 0px" }}
          >
            <p style={{ fontSize: "14px", fontWeight: 500 }}>AI Features</p>
          </Divider>
        </Col>
        <Row className="px-3 pt-1">
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Biometric"
              title="Enable biometric authentication for secure access."
              name={["appSubFeature", "biometric"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Accabot chat"
              title="Enable AI-powered Accabot assistant."
              name={["appSubFeature", "accabotChat"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Auto complete"
              title="Enable auto-complete functionality for form fields."
              name={["appSubFeature", "autoComplete"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>

          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="AI assistance for emails"
              title="Automatically summarize email content using AI."
              name={["appSubFeature", "emailSummarizer"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Book OCR text"
              title="Extract text from book images using OCR technology."
              name={["appSubFeature", "bookOcrText"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Call transcription"
              title="Automatically transcribe call recordings."
              name={["appSubFeature", "callTranscription"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>

          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Team isolation"
              title="Isolate team data and restrict cross-team access."
              name={["appSubFeature", "teamIsolation"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
        </Row>
        {/* Client fields */}
        <Col className="font-bold d-flex px-3 pt-1" span={24}>
          <Divider
            orientation="left"
            orientationMargin="0"
            style={{ margin: "0px 0px 0px 0px" }}
          >
            <p style={{ fontSize: "14px", fontWeight: 500 }}>Client fields</p>
          </Divider>
        </Col>
        <Row className="px-3 pt-1 align-items-center">
          {/* <Col span={2} className="d-flex justify-content-end">
            <Checkbox
              disabled={isEdit.type !== PracticeUpdateTypes.praticeProperites}
              checked={clientFieldKeys.every((key) =>
                form.getFieldValue(["appSubFeature", key]),
              )}
              indeterminate={
                clientFieldKeys.some((key) =>
                  form.getFieldValue(["appSubFeature", key]),
                ) &&
                !clientFieldKeys.every((key) =>
                  form.getFieldValue(["appSubFeature", key]),
                )
              }
              onChange={(e) => {
                const checked = e.target.checked;
                const updated: any = {};
                clientFieldKeys.forEach((key) => {
                  updated[key] = checked;
                });
                form.setFieldsValue({
                  appSubFeature: {
                    ...form.getFieldValue("appSubFeature"),
                    ...updated,
                  },
                });
              }}
            >
              Select All
            </Checkbox>
          </Col> */}
        </Row>
        <Row className="px-3 pt-1">
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appSubFeature", "firstResponse"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.praticeProperites
                    ? false
                    : true
                }
                className="line-height-28"
              >
                First response
              </Checkbox>
            </Form.Item>
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appSubFeature", "accountManagar"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.praticeProperites
                    ? false
                    : true
                }
                className="line-height-28"
              >
                Account managar
              </Checkbox>
            </Form.Item>
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appSubFeature", "accountant"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.praticeProperites
                    ? false
                    : true
                }
                className="line-height-28"
              >
                Accountant
              </Checkbox>
            </Form.Item>
          </Col>

          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appSubFeature", "mode"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.praticeProperites
                    ? false
                    : true
                }
                className="line-height-28"
              >
                Mode
              </Checkbox>
            </Form.Item>
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appSubFeature", "agentCredentialsRemark"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.praticeProperites
                    ? false
                    : true
                }
                className="line-height-28"
              >
                Agent credentials remark
              </Checkbox>
            </Form.Item>
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appSubFeature", "clientBrand"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.praticeProperites
                    ? false
                    : true
                }
                className="line-height-28"
              >
                Client brand
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>
        {/* Tax and Accounts */}
        <Col className="font-bold d-flex px-3 pt-1" span={24}>
          <Divider
            orientation="left"
            orientationMargin="0"
            style={{ margin: "0px 0px 0px 0px" }}
          >
            <p style={{ fontSize: "14px", fontWeight: 500 }}>
              Tax and Accounts
            </p>
          </Divider>
        </Col>

        <Row className="px-3 pt-1">
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="S455 tax liability"
              name={["appSubFeature", "s455TaxLiability"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Accabot"
              name={["appSubFeature", "accaBot"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label="Xcelify"
              name={["appSubFeature", "xcelify"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <LabelCheckboxwithTooltip
              label=" Journal line description"
              title="While preparing trial balance, in additional to an overall description for journal, this helps in enabling/disabling description for each line of the journal"
              name={["appSubFeature", "journalDescription"]}
              isEdit={isEdit}
              form={form}
            />
          </Col>
        </Row>

        <Col className="font-bold d-flex px-3 pt-1" span={24}>
          <Divider
            orientation="left"
            orientationMargin="0"
            style={{ margin: "0px 0px 0px 0px" }}
          >
            <p style={{ fontSize: "14px", fontWeight: 500 }}>Payroll</p>
          </Divider>
        </Col>
        <Row className="px-3 pt-1">
          <Col lg={8} sm={12} xs={24} className="d-flex">
            <Form.Item
              className="custom-form-item"
              name={["appSubFeature", "payeMasterReport"]}
              valuePropName="checked"
            >
              <Checkbox
                disabled={
                  isEdit.type === PracticeUpdateTypes.praticeProperites
                    ? false
                    : true
                }
                className="line-height-28"
              >
                Subscribe PAYE Reports
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>
        <Col className="font-bold d-flex px-3 pt-1" span={24}>
          <Divider
            orientation="left"
            orientationMargin="0"
            style={{ margin: "0px 0px 0px 0px" }}
          >
            <p style={{ fontSize: "14px", fontWeight: 500 }}>
              Client logo dimensions  
            </p>
          </Divider>
        </Col>

        <Row className="px-3">
          {/* <Col lg={8} sm={12} xs={24}> */}
          <Form.Item
            name={["clientLogoDimensions", "height"]}
            label={<span style={{ paddingTop: "8px" }}> Height</span>}
            wrapperCol={{ xxl: 20, sm: 20, xs: 22 }}
            layout="vertical"
          >
            <Input
              // className="line-height-28"
              style={{ width: "100%", height: "28px" }}
              addonAfter="Pixel(s)"
              placeholder="Enter value"
              disabled={
                isEdit.type === PracticeUpdateTypes.praticeProperites
                  ? false
                  : true
              }
              onKeyPress={(e) => Utils.ValidateNumberOnly(e)}
            />
          </Form.Item>

          {/* </Col>
          <Col lg={8} sm={12} xs={24}> */}
          <Form.Item
            label={<span style={{ paddingTop: "8px" }}>Width</span>}
            name={["clientLogoDimensions", "width"]}
            layout="vertical"
            wrapperCol={{ xxl: 20, sm: 20, xs: 22 }}
          >
            <Input
              // className="line-height-28"
              style={{ width: "100%" }}
              addonAfter="Pixel(s)"
              placeholder="Enter value"
              disabled={
                isEdit.type === PracticeUpdateTypes.praticeProperites
                  ? false
                  : true
              }
              onKeyPress={(e) => Utils.ValidateNumberOnly(e)}
            />
          </Form.Item>
          {/* </Col> */}
        </Row>

        {!isViewOnly &&
          isEdit.type === PracticeUpdateTypes.praticeProperites && (
            <Row className="px-3 pt-4">
              <Button type="primary" onClick={saveFeatureInformation}>
                <SaveOutlined /> Save
              </Button>
              <Button className="ms-2" onClick={handleCancel}>
                <CloseOutlined /> Cancel
              </Button>
            </Row>
          )}
      </Col>
    </Form>
  );
};

export default Features;

export const LabelInputwithTooltip = ({
  label,
  title,
  name,
  isEdit,
  form,
}: any) => {
  return (
    <>
      <Form form={form}>
        <Form.Item
          className="custom-form-item"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 14 }}
          label={
            <div
              className="d-flex"
              style={{ alignItems: "center", gap: "6px" }}
            >
              <span>{label}</span>
              <Tooltip
                id="tooltip-ptt"
                overlayClassName="white-tooltip"
                title={title}
              >
                <InfoCircleOutlined style={{ fontSize: "13px" }} />
              </Tooltip>
            </div>
          }
          name={name}
        >
          <Input
            className="line-height-28"
            placeholder={label}
            style={{ width: "140px" }}
            onKeyPress={(e) => Utils.ValidateNumberOnly(e)}
            disabled={
              isEdit.type === PracticeUpdateTypes.praticeProperites
                ? false
                : true
            }
          />
        </Form.Item>
      </Form>
    </>
  );
};

export const LabelCheckboxwithTooltip = ({
  label,
  title,
  name,
  isEdit,
  form,
}: any) => {
  const checked = Form.useWatch(name, form) || false;
  const handleChange = (e: any) => {
    form?.setFieldValue(name, e?.target?.checked);
  };
  return (
    <>
      <Form.Item
        className="custom-form-item"
        name={name}
        valuePropName="checked"
      >
        <Checkbox
          checked={checked ? true : false}
          disabled={
            isEdit.type === PracticeUpdateTypes.praticeProperites ? false : true
          }
          className="line-height-28"
          onChange={handleChange}
        >
          {label}
        </Checkbox>
        <Tooltip
          id="tooltip-ptt"
          overlayClassName="white-tooltip"
          title={title}
        >
          <InfoCircleOutlined style={{ fontSize: "13px" }} />
        </Tooltip>
      </Form.Item>
    </>
  );
};
